import { 
  Box, 
  Button, 
  Card, 
  FormControlLabel,
  MenuItem,
  Stack,
  Switch, 
  Table, 
  TableBody, 
  TableContainer, 
  TablePagination, 
  Typography
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import Iconify from 'src/components/Iconify';
import { LoadingTableRow, TableEmptyRows, TableHeadCustom, TableNoData } from "src/components/table";
import useTable, { emptyRows } from "src/hooks/useTable";
import FhirOrganizationTableToolbar from "../common/FhirOrganizationTableToolbar";
import { cleanSearchInput } from "src/utils/string";
import AffiliateTableRow from "./AffiliateTableRow";
import FhirOrganizationListModal from "../common/FhirOrganizationListModal";
import { WrappedOrganization } from "src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization";
import { OrganizationAffiliation } from "src/careflow/ph-core/fhir-client/resources";
import { useOrganizationAffiliations } from "src/careflow/ph-core/fhir-client/fhir-react";
import useGridFilters from "src/careflow/ph-core/grid/hook/useGridFilters";
import { fetchOrganizationAffiliationByPrimaryOrg, translateTableHead } from "src/careflow/helpers/common";
import { checkAclValidation } from "src/careflow/ph-core/admin/permissions/util/permission.utils";
import crsAcls from "src/careflow/ph-core/admin/permissions/util/crs/crsAcls";
import { TABLE_HEAD_ORGANIZATION_AFFILIATION } from "src/careflow/common/table-head";

type Props = {
  organization: {
    main: WrappedOrganization | null;
    partOf: WrappedOrganization | null;
    childs: WrappedOrganization[] | null;
  };
  translate: any;
};

export interface OrganizationAffilitianWithResources extends OrganizationAffiliation {
  primaryOrganization: WrappedOrganization | null;
  participatingOrg: WrappedOrganization | null;
}

export function AffiliateList ({ 
  organization,
  translate
}: Props) {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable();

  const [, { create, update }] = useOrganizationAffiliations({ autofetch: false });

  const [openAffiliateModal, setOpenAffiliateModal] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<OrganizationAffilitianWithResources[] | null>(null);
  const { searchTextFieldValue, onSearchTextFieldChange } = useGridFilters({ searchTextFieldValue: null });

  const { enqueueSnackbar } = useSnackbar();

  const handleActiveAffiliation = async (row: OrganizationAffilitianWithResources, active: boolean) => {
    setRefresh(true);

    const { primaryOrganization: _, participatingOrg: __,  ...rest } = row;
    try {

      await update({
        ...rest,
        active
      });

      enqueueSnackbar(translate('dialogs.affiliateUpdated'));
      setRefresh(false);
    } catch (error) {
      setRefresh(false);
      enqueueSnackbar(translate('dialogs.error'), { variant: 'error' });
    }
  };

  const handleAddAffiliation = async (row: any) => {
    setRefresh(true);
    try {

      const orgSelectedMapn = row?.map((org) => ({
        active: true,
        organization: {
          reference: `Organization/${organization?.main?.id}`
        },
        participatingOrganization: {
          reference: `Organization/${org?.id}`
        },
        resourceType: "OrganizationAffiliation"
      }));

      await create(orgSelectedMapn);

      const count = row?.length ?? 0;

      const message = count === 1
        ? translate('dialogs.affiliateLinked')
        : translate('dialogs.affiliateUnlinked');

      enqueueSnackbar(message);
      setRefresh(false);
    } catch (error) {
      setRefresh(false);
      enqueueSnackbar(translate('dialogs.error'), { variant: 'error' });
    }
  };

  useEffect(() => {
    const getOrganizationAffiliationAsync = async () => {
      if(!organization?.main?.id && !refresh) return;
      setIsLoading(true);

      const organizationAffiliation = 
        await fetchOrganizationAffiliationByPrimaryOrg(organization?.main?.id ?? '');
  
      setData(organizationAffiliation);
      setIsLoading(false);
    };
    getOrganizationAffiliationAsync();
  },[organization?.main?.id, refresh]);

  const orgsAffiFiltered = useMemo(() => {
    const searchInput = cleanSearchInput(
      searchTextFieldValue ?? ''
    ).toLowerCase();
  
    return (data ?? [])
      .filter(org => filterOrgAffiBySearchInput(searchInput, org));
  }, [data, searchTextFieldValue]);

  const getIdsAllOrg = useMemo<string[]>(() => {
    return [
      organization?.main?.id,
      ...(data?.map(child => child?.participatingOrg?.id) ?? [])
    ].filter((id): id is string => Boolean(id));
  }, [data]);

  const isNotFound = !orgsAffiFiltered.length;

  const isAllowedEdit = checkAclValidation({
    acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.AFFILIATE_ORGANIZATIONS.EDIT],
  });

  const isAllowedAdd = checkAclValidation({
    acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.AFFILIATE_ORGANIZATIONS.ADD],
  });

  return(
    <Card sx={{ width: '100%', height: '100%', paddingTop: 3 }}>
      <FhirOrganizationTableToolbar 
        searchTextFieldProps={{
          placeholder: translate('list.filter.searchFieldPlaceholder'),
          onChange: (event) => {
            onSearchTextFieldChange(event.target.value);
            setPage(0);
          },
        }}
        containerSx={{ marginBottom: 3, paddingX: 2.5 }}
        childrenFilter={
          <Button
            disabled={!isAllowedAdd}
            onClick={() => setOpenAffiliateModal(true)}
            variant="contained"
            startIcon={<Iconify icon={'eva:plus-fill'} />}
          >
            {translate('details.affiliate.addButton')}
          </Button>
        } 
      />

      <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
        <Table size={dense ? 'small' : 'medium'}>
          <TableHeadCustom
            order={order}
            orderBy={orderBy}
            headLabel={translateTableHead(TABLE_HEAD_ORGANIZATION_AFFILIATION, translate, 'translations')}
            rowCount={organization?.childs?.length}
            onSort={onSort}
          />

          {!isLoading ? (
            <TableBody>
              {orgsAffiFiltered
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <AffiliateTableRow
                    key={row.id}
                    row={row}
                    onDeleteRow={() => {}}
                    onEditRow={() => {}}
                    menuItems={
                      <>
                        {isAllowedEdit &&
                          <MenuItem
                            onClick={() => handleActiveAffiliation(row, !row?.active)}
                          >
                            <Stack direction="row" spacing={2} alignItems="center">
                              {row?.active ? (
                                <Iconify 
                                  icon="eva:trash-2-outline" 
                                  sx={{ fontSize: 20 }}
                                  color={row?.active ? 'red' : 'green'}
                                />
                              ) : (
                                <Iconify 
                                  icon="eva:checkmark-circle-2-outline" 
                                  sx={{ fontSize: 20,  }}
                                  color={row?.active ? 'red' : 'green'}
                                />
                              )}
                              <Typography variant="body2" color={row?.active ? 'red' : 'green'}>
                              {row?.active 
                                ? translate('details.affiliate.deactivate')
                                : translate('details.affiliate.activate') 
                              }
                              </Typography>
                            </Stack>
                          </MenuItem>
                        }
                      </>
                    }
                    translate={translate}
                  />
              ))}

              <TableNoData isNotFound={isNotFound} />
            </TableBody>
          ) : (
            <LoadingTableRow colSpan={TABLE_HEAD_ORGANIZATION_AFFILIATION.length} />
          )}
        </Table>
      </TableContainer>

      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={orgsAffiFiltered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />

        <FormControlLabel
          control={<Switch checked={dense} onChange={onChangeDense} />}
          label="Dense"
          sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
        />
      </Box>
      <FhirOrganizationListModal 
        open={openAffiliateModal}
        translate={translate}
        handleAction={handleAddAffiliation}
        onClose={() => setOpenAffiliateModal(false)}
        filterIdsOrg={getIdsAllOrg}
      />
    </Card>
  )
}

const filterOrgAffiBySearchInput = (
  searchInput: string,
  data: OrganizationAffilitianWithResources
): boolean => {

  if (!searchInput || searchInput.length < 3) return true;
  
  const searcheableValues: string[] = [
    data?.primaryOrganization?.name ?? '',
    data?.participatingOrg?.name ?? ''
  ];

  for (const value of searcheableValues) {
    if (value.toLowerCase().includes(searchInput)) return true;
  }

  return false;
}